// @flow
import React, { Component } from 'react';
import BrowserDetector from 'app/common/lib/BrowserDetector';
import i18n from 'app/common/lib/i18n';
import type { TriedItItemType } from 'app/common/react/components/do/aggregated-pins/did-it/common/DidItFeedItem';
import VideoWithContextLogging from 'app/common/react/components/video/VideoWithContextLogging';
import { Mask, Touchable } from 'gestalt';

type Props = {|
  item: TriedItItemType,
  viewType: string,
|};

type State = {|
  isFullscreen: boolean,
  isPlaying: boolean,
  isReady: boolean,
  showControls: boolean,
  volumeLevel: number,
|};

type onTouchEvent = {
  event: SyntheticMouseEvent<HTMLDivElement> | SyntheticKeyboardEvent<HTMLDivElement>,
};

type onPlayPauseEvent = {
  event: SyntheticEvent<HTMLDivElement>,
};

type onEndedEvent = {
  event: SyntheticEvent<HTMLVideoElement>,
};

type onToggleFullscreenEvent = {
  event: Event,
  fullscreen: boolean,
};

type onVolumeChangeEvent = {
  event: SyntheticEvent<HTMLDivElement>,
  volume: number,
};

export default class TriedItVideo extends Component<Props, State> {
  state: State = {
    isFullscreen: false,
    isPlaying: false,
    isReady: false,
    showControls: true,
    volumeLevel: 0,
  };

  handleTouch = (e: onTouchEvent) => {
    e.event.stopPropagation();
    this.setState((prevState: State) => ({ isPlaying: !prevState.isPlaying }));
  };

  handleShowControls = () => this.setState({ showControls: true });

  handleHideControls = () => this.setState({ showControls: false });

  handleEnded = (e: onEndedEvent) => {
    e.event.stopPropagation();
    this.setState({ isPlaying: false });
  };

  handlePause = (e: onPlayPauseEvent) => {
    e.event.stopPropagation();
    this.setState({ isPlaying: false });
  };

  handlePlay = (e: onPlayPauseEvent) => {
    e.event.stopPropagation();
    this.setState({ isPlaying: true });
  };

  handleToggleFullscreen = (e: onToggleFullscreenEvent) => {
    const { event, fullscreen } = e;
    event.stopPropagation();
    this.setState({ isFullscreen: fullscreen });
  };

  handleVolumeChange = (e: onVolumeChangeEvent) => {
    const { event, volume } = e;
    event.stopPropagation();
    this.setState({ volumeLevel: volume });
  };

  render() {
    const { item, viewType } = this.props;
    const { videos: itemVideos } = item;
    if (!itemVideos || itemVideos.length === 0) {
      // should never happen
      return null;
    }
    const video = itemVideos[0];
    const { showControls, isFullscreen, isPlaying, volumeLevel } = this.state;
    const isSafari = BrowserDetector.isSafari();

    const { V_HLSV4: hls } = video.video_list;
    // This should not happen but flow wants to handle this case
    if (!hls) {
      return null;
    }
    const { height, width, thumbnail, url } = hls || {};
    const src = [{ type: 'video/m3u8', src: url }];

    return (
      <Touchable
        shape="rounded"
        onTouch={this.handleTouch}
        onMouseEnter={this.handleShowControls}
        onMouseLeave={this.handleHideControls}
      >
        <Mask
          shape="rounded"
          // #SafariNeedsWillChangeTransformFalse
          // need to set willChange:transform false for Safari fullscreen (BUG-94602)
          willChangeTransform={!(isSafari && isFullscreen)}
          wash
        >
          <VideoWithContextLogging
            contextLogData={{
              view: viewType,
              viewParameter: PCONST.thrift.ViewParameterType.USER_DID_IT,
              component: PCONST.thrift.ComponentType.USER_DID_IT_ACTIVITY,
              objectId: item.id,
              video_id: video.id,
            }}
            accessibilityMaximizeLabel={i18n._('Maximize', 'Maximize button on video')}
            accessibilityMinimizeLabel={i18n._('Minimize', 'Minimize button on video')}
            accessibilityMuteLabel={i18n._('Mute', 'Mute button on video')}
            accessibilityPauseLabel={i18n._('Pause', 'Pause button on video')}
            accessibilityPlayLabel={i18n._('Play', 'Play button on video')}
            accessibilityUnmuteLabel={i18n._('Unmute', 'Unmute button on video')}
            aspectRatio={Math.max(width / height, 1)}
            captions=""
            controls={showControls}
            onEnded={this.handleEnded}
            onFullscreenChange={this.handleToggleFullscreen}
            onPause={this.handlePause}
            onPlay={this.handlePlay}
            onVolumeChange={this.handleVolumeChange}
            playing={isPlaying}
            poster={thumbnail}
            src={src}
            volume={volumeLevel}
          />
        </Mask>
      </Touchable>
    );
  }
}
